/**
 * Push Notifications Service
 * Handles push notification setup and management for web and mobile platforms
 */

import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { LocalNotifications, ScheduleOptions } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { logger } from '../utils/logger';

export interface NotificationSchedule {
  id: string;
  title: string;
  body: string;
  schedule: {
    hour: number;
    minute: number;
    repeats: boolean;
  };
  data?: Record<string, any>;
}

export interface NotificationPermissionStatus {
  granted: boolean;
  denied: boolean;
  prompt: boolean;
  platform: 'web' | 'mobile';
}

/**
 * Check if push notifications are supported on the current platform
 */
export const isPushNotificationSupported = (): boolean => {
  if (Capacitor.isNativePlatform()) {
    return true; // Capacitor handles this
  }

  // Web platform checks
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermissionStatus = async (): Promise<NotificationPermissionStatus> => {
  try {
    if (Capacitor.isNativePlatform()) {
      // Mobile platform - use Local Notifications for permission check
      const permStatus = await LocalNotifications.checkPermissions();
      return {
        granted: permStatus.display === 'granted',
        denied: permStatus.display === 'denied',
        prompt: permStatus.display === 'prompt',
        platform: 'mobile'
      };
    } else {
      // Web platform - use Web API
      const permission = Notification.permission;
      return {
        granted: permission === 'granted',
        denied: permission === 'denied',
        prompt: permission === 'default',
        platform: 'web'
      };
    }
  } catch (error) {
    logger.error('Error checking notification permissions', error);
    return {
      granted: false,
      denied: true,
      prompt: false,
      platform: Capacitor.isNativePlatform() ? 'mobile' : 'web'
    };
  }
};

/**
 * Request notification permissions
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  try {
    logger.debug('Requesting notification permissions');

    if (Capacitor.isNativePlatform()) {
      // Mobile platform - use Local Notifications
      const permResult = await LocalNotifications.requestPermissions();
      const granted = permResult.display === 'granted';

      if (granted) {
        logger.debug('Mobile local notification permissions granted');
        await initializeLocalNotifications();
      } else {
        logger.warn('Mobile local notification permissions denied');
      }

      return granted;
    } else {
      // Web platform - use Web API
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';

      if (granted) {
        logger.debug('Web notification permissions granted');
        await initializeWebNotifications();
      } else {
        logger.warn('Web notification permissions denied');
      }

      return granted;
    }
  } catch (error) {
    logger.error('Error requesting notification permissions', error);
    return false;
  }
};

/**
 * Initialize local notifications for mobile platforms
 */
const initializeLocalNotifications = async (): Promise<void> => {
  try {
    logger.debug('Initializing mobile local notifications');

    // Listen for notification actions
    LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
      logger.debug('Local notification action performed:', notification);
      handleNotificationAction(notification.notification.extra);
    });

    // Listen for notifications received while app is in foreground
    LocalNotifications.addListener('localNotificationReceived', (notification) => {
      logger.debug('Local notification received:', notification);
    });

    logger.debug('Mobile local notifications initialized');
  } catch (error) {
    logger.error('Error initializing local notifications', error);
  }
};

/**
 * Initialize push notifications for mobile platforms (for remote push - requires Firebase)
 * Note: Currently unused - will be enabled when Firebase Cloud Messaging is configured
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const initializePushNotifications = async (): Promise<void> => {
  try {
    logger.debug('Initializing mobile push notifications');

    // Register with Apple / Google to receive push via APNS/FCM
    await PushNotifications.register();

    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration', (token: Token) => {
      logger.debug('Push registration success, token: ' + token.value);
      // Store token for backend communication
      localStorage.setItem('push-token', token.value);
    });

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (error: any) => {
      logger.error('Error on registration: ' + JSON.stringify(error));
    });

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      logger.debug('Push notification received: ', notification);

      // Show local notification if app is in foreground
      showLocalNotification({
        title: notification.title || 'Prayer Reminder',
        body: notification.body || 'Time for your daily prayers',
        data: notification.data
      });
    });

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      logger.debug('Push notification action performed: ', notification);

      // Handle notification tap - navigate to appropriate screen
      handleNotificationAction(notification.notification.data);
    });

  } catch (error) {
    logger.error('Error initializing push notifications', error);
  }
};

/**
 * Initialize web notifications
 */
const initializeWebNotifications = async (): Promise<void> => {
  try {
    logger.debug('Initializing web notifications');

    // Register service worker for web push
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      logger.debug('Service worker ready for notifications', registration);
    }
  } catch (error) {
    logger.error('Error initializing web notifications', error);
  }
};

/**
 * Show a local notification immediately
 */
export const showLocalNotification = async (notification: {
  title: string;
  body: string;
  data?: Record<string, any>;
}): Promise<void> => {
  try {
    if (Capacitor.isNativePlatform()) {
      // Mobile platform - use Local Notifications plugin
      // For immediate notifications, schedule 1 second in the future
      const scheduleTime = new Date(Date.now() + 1000);

      await LocalNotifications.schedule({
        notifications: [
          {
            id: Math.floor(Math.random() * 100000), // Random ID to avoid conflicts
            title: notification.title,
            body: notification.body,
            extra: notification.data,
            schedule: {
              at: scheduleTime,
              allowWhileIdle: true,
            },
            sound: 'default',
            // Use custom notification icon (white monochrome for status bar)
            smallIcon: 'ic_notification',
            largeIcon: 'ic_launcher',
            iconColor: '#4F46E5',
          }
        ]
      });
      logger.debug('Mobile local notification scheduled:', notification);
    } else {
      // Web platform - use Web Notification API
      const permission = await getNotificationPermissionStatus();
      if (permission.granted) {
        new Notification(notification.title, {
          body: notification.body,
          icon: '/icons/icon-192.webp',
          badge: '/icons/icon-192.webp',
          data: notification.data,
          tag: 'prayer-reminder'
        });
      }
    }
  } catch (error) {
    logger.error('Error showing local notification', error);
  }
};

/**
 * Schedule daily prayer reminders
 */
export const scheduleDailyPrayerReminders = async (times: Array<{hour: number, minute: number}>): Promise<void> => {
  try {
    logger.debug('Scheduling daily prayer reminders', times);

    // Store reminder times in localStorage
    localStorage.setItem('prayer-reminder-times', JSON.stringify(times));

    if (Capacitor.isNativePlatform()) {
      // Mobile platform - use Local Notifications plugin for scheduling
      // First, cancel any existing scheduled notifications
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({ notifications: pending.notifications });
      }

      // Schedule new notifications
      const notifications: ScheduleOptions['notifications'] = times.map((time, index) => ({
        id: 1000 + index, // Use consistent IDs for daily reminders
        title: 'Prayer Reminder 🙏',
        body: 'Time for your daily prayers with Ora',
        extra: { type: 'prayer-reminder', time: `${time.hour}:${time.minute}` },
        schedule: {
          on: {
            hour: time.hour,
            minute: time.minute,
          },
          repeats: true,
          allowWhileIdle: true,
        },
        sound: 'default',
        smallIcon: 'ic_notification',
        largeIcon: 'ic_launcher',
        iconColor: '#4F46E5',
      }));

      await LocalNotifications.schedule({ notifications });
      logger.debug('Mobile notifications scheduled for times:', times);
    } else {
      // Web platform - set up interval checking
      scheduleWebNotificationChecks(times);
    }
  } catch (error) {
    logger.error('Error scheduling prayer reminders', error);
  }
};

/**
 * Schedule web notification checks
 */
const scheduleWebNotificationChecks = (times: Array<{hour: number, minute: number}>): void => {
  // Clear existing intervals
  const existingInterval = localStorage.getItem('notification-interval-id');
  if (existingInterval) {
    clearInterval(Number(existingInterval));
  }

  // Check every minute for prayer times
  const intervalId = setInterval(() => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    times.forEach(time => {
      if (time.hour === currentHour && time.minute === currentMinute) {
        // Check if we haven't already sent this notification today
        const lastNotificationKey = `last-prayer-notification-${time.hour}-${time.minute}`;
        const lastNotification = localStorage.getItem(lastNotificationKey);
        const today = new Date().toDateString();

        if (lastNotification !== today) {
          showLocalNotification({
            title: 'Prayer Reminder 🙏',
            body: 'Time for your daily prayers with Ora',
            data: { type: 'prayer-reminder', time: `${time.hour}:${time.minute}` }
          });
          localStorage.setItem(lastNotificationKey, today);
        }
      }
    });
  }, 60000); // Check every minute

  localStorage.setItem('notification-interval-id', intervalId.toString());
};

/**
 * Handle notification action when user taps on notification
 */
const handleNotificationAction = (data: Record<string, any> | undefined): void => {
  if (!data) return;

  logger.debug('Handling notification action', data);

  // Navigate based on notification type
  switch (data.type) {
    case 'prayer-reminder':
      // Could navigate to prayer screen
      // This would be handled by your app's navigation system
      logger.debug('Prayer reminder notification tapped');
      break;
    case 'novena-reminder':
      // Navigate to novena tracker
      logger.debug('Novena reminder notification tapped');
      break;
    default:
      logger.debug('Unknown notification type:', data.type);
  }
};

/**
 * Clear all scheduled notifications
 */
export const clearAllNotifications = async (): Promise<void> => {
  try {
    logger.debug('Clearing all notifications');

    // Clear web notification intervals
    const intervalId = localStorage.getItem('notification-interval-id');
    if (intervalId) {
      clearInterval(Number(intervalId));
      localStorage.removeItem('notification-interval-id');
    }

    // Clear stored reminder times
    localStorage.removeItem('prayer-reminder-times');

    // Clear last notification timestamps
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('last-prayer-notification-')) {
        localStorage.removeItem(key);
      }
    });

    if (Capacitor.isNativePlatform()) {
      // Mobile platform - clear all scheduled notifications
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({ notifications: pending.notifications });
      }
      logger.debug('Cleared mobile scheduled notifications');
    }
  } catch (error) {
    logger.error('Error clearing notifications', error);
  }
};

/**
 * Get current notification settings
 */
export const getNotificationSettings = async (): Promise<{
  enabled: boolean;
  times: Array<{hour: number, minute: number}>;
  permission: string;
}> => {
  const times = localStorage.getItem('prayer-reminder-times');
  const permStatus = await getNotificationPermissionStatus();

  return {
    enabled: !!times && permStatus.granted,
    times: times ? JSON.parse(times) : [],
    permission: permStatus.granted ? 'granted' : permStatus.denied ? 'denied' : 'default'
  };
};

/**
 * Test notification - for debugging
 */
export const sendTestNotification = async (): Promise<void> => {
  await showLocalNotification({
    title: 'Test Notification 🔔',
    body: 'This is a test notification from Ora',
    data: { type: 'test' }
  });
};
